/** @jsxImportSource @emotion/react */

import React from "react";
import Immutable from "immutable";
import {LicenseManager, ModuleRegistry, AllEnterpriseModule} from "ag-grid-enterprise";
import {AgChartsEnterpriseModule} from "ag-charts-enterprise";
import {css, Global} from "@emotion/react";

import "css/ag-grid-ba.css";

import * as ajax from "js/common/ajax";
import {columnSpacing, dashboardColumns} from "js/dashboards/utils";
import {indexBy} from "js/common/utils/collections";
import {getUniqueName} from "js/common/utils/unique-naming";
import useBooleanState from "js/common/hooks/use-boolean-state";
import * as DashboardUtils from "js/dashboards/utils";
import * as TabCache from "js/dashboards/tab-cache";
import * as SavedConfigs from "js/common/saved-configs";
import * as Rata from "js/common/utils/remote-data";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as Permissions from "js/common/permissions";
import * as Users from "js/common/users";
import * as Auditor from "js/common/auditor";
import * as TagRepo from "js/common/repo/backbone/tag-repo";
import * as ScheduledReports from "js/common/scheduled-reports";

import uiGridTemplate from "js/dashboards/components/grids/ui-grid";
import asyncServerGridTemplate from "js/dashboards/components/grids/async-server-grid";
import metricCurveTemplate from "js/dashboards/components/metric-curve";
import ratioTemplate from "js/dashboards/components/ratios/ratio";
import ratioComparisonTemplate from "js/dashboards/components/ratio-comparison/ratio-comparison";
import leaderboardTemplate from "js/dashboards/components/leaderboard/leaderboard";
import textTemplate from "js/dashboards/components/text";
import dividerTemplate from "js/dashboards/components/divider";
import trendTemplate from "js/dashboards/components/trends/trend";
import performanceSnapshotTemplate from "js/dashboards/components/performance-snapshot/performance-snapshot";

import MenuBar from "js/common/views/menu-bar";
import NovoLoading from "js/dashboards/novo-loading";
import DashboardToolbar from "js/dashboards/toolbar/dashboard-toolbar";
import TabBar from "js/dashboards/toolbar/tab-bar";
import DashboardSidebar, {COLLAPSED_WIDTH, EXPANDED_WIDTH} from "js/dashboards/sidebar/dashboard-sidebar";
import DataConfigToolbar from "js/dashboards/data-config-toolbar";
import DashboardDisplay from "js/dashboards/dashboard-display";
import Dialog from "js/common/views/tabs-dialog";
import EasyShareDrawer from "js/common/views/sharing/easy-share-drawer";
import AddComponentDrawer from "js/dashboards/add-component-drawer";
import Overlay from "js/common/views/overlay";
import EditComponentDrawer from "js/dashboards/edit-component-drawer";

import {TextButton} from "js/common/views/inputs/buttons";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import useDimensions from "js/common/utils/use-dimensions";
import DashboardFallback from "js/dashboards/dashboard-fallback";
import {currentQueryString} from "js/common/utils/query-strings";
import AiAssistantDrawer from "js/dashboards/ai-assistant/ai-assistant-drawer";
import currentClient from "js/common/repo/backbone/current-client";
import {clientPermissions} from "js/common/permissions";
import AiAssistantButton from "js/dashboards/toolbar/ai-assistant-button";
import ScheduleDrawer from "js/dashboards/scheduling/schedule-drawer";

const _ = window._;

const PANEL_TYPE_WEIGHTS = {
  MetricCurve: 1,
  Trend: 2,
  Ratio: 2,
  RatioComparison: 2,
  Leaderboard: 3,
  PerformanceSnapshot: 5,
  UiGrid: 10,
  AsyncServerGrid: 10,
};
const DEFAULT_PANEL_WEIGHT = 5;

const getPanelWeight = type => PANEL_TYPE_WEIGHTS[type] ?? DEFAULT_PANEL_WEIGHT;

LicenseManager.setLicenseKey(
    "Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-117552}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Bullhorn,_Inc.}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Bullhorn_Analytics}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Bullhorn_Analytics}_need_to_be_licensed___{Bullhorn_Analytics}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{16_August_2027}____[v3]_[0102]_MTgxODM3MDgwMDAwMA==62b46a8e9a4d48b26a8c9cd07421b185"
);
ModuleRegistry.registerModules([
  AllEnterpriseModule.with(AgChartsEnterpriseModule)
]);

const ownershipTypeToCustomDisplayName = Immutable.fromJS({ownedConfigs: "My Dashboards"});

const globalCss = theme => css`
  body,
  #content-region {
    height: 100%;
    -webkit-print-color-adjust: exact;
    background-color: ${theme.palette.background.paper};
  }

  #content-region > div {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  // Hide swipeable drawer area on dashboards
  .PrivateSwipeArea-root {
    display: none;
  }
`;

const DashboardApp = () => {
  const isScheduledReportMode = currentQueryString.scheduledreport?.toLowerCase() === "true";

  const {
    flag: showSavedDashboards,
    setTrue: openSavedDashboardsDrawer,
    setFalse: closeSavedDashboardsDrawer
  } = useBooleanState();

  const [isSavingDashboard, setSavingDashboard] = React.useState(false);
  const [isEditing, setEditing] = React.useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = React.useState(false);
  const [dashboardIdToScheduledReports, setDashboardIdToScheduledReports] = React.useState(Immutable.Map());

  const scrollToComponentRef = React.useRef();
  const scrollContainerRef = React.useRef();
  const {theme} = React.useContext(CustomThemeContext);
  const componentIdToLoadIdRef = React.useRef(Immutable.Map());

  const captureUiStateRefs = React.useRef(Immutable.Map());
  const setCaptureUiStateRef = React.useCallback((componentId, captureFn) => {
    if (captureFn) {
      captureUiStateRefs.current = captureUiStateRefs.current.set(componentId, captureFn);
    } else {
      captureUiStateRefs.current = captureUiStateRefs.current.delete(componentId);
    }
  }, []);

  const [messagesToDashboardsWrapper, setMessagesToDashboardsWrapper] = React.useState(() =>
      Rata.wrapInitialValue(Immutable.Map())
  );

  const [ownershipTypeToDashboardsWrapper, setOwnershipTypeToDashboardsWrapper] = React.useState(() =>
      Rata.wrapInitialValue(Immutable.Map())
  );

  const loadAndSetDashboards = React.useCallback(() => {
    setOwnershipTypeToDashboardsWrapper((wrapper) => Rata.toLoading(wrapper));
    loadDashboards().then((result) => {
      setOwnershipTypeToDashboardsWrapper((wrapper) => Rata.toLoaded(wrapper, result));
    });
  }, []);

  const loadScheduledReports = React.useCallback(() => {
    if (Users.currentHasPermission(Permissions.userPermissions.canScheduleReport)) {
      ScheduledReports.getAllScheduledReports().then(
          (reports) => {
            const byDashboard = reports.groupBy((report) => report.get("dashboardId"));
            setDashboardIdToScheduledReports(byDashboard);
          },
          () => console.log("Failed to load scheduled reports")
      );
    }
  }, []);

  const loadDashboardsAndScheduledReports = React.useCallback(() => {
    loadAndSetDashboards();
    loadScheduledReports();
  }, [loadAndSetDashboards, loadScheduledReports]);

  // Sets and removes className for use in gainsight
  React.useEffect(() => {
    document.body.classList.add("dashboards");
    return function cleanup() {
      document.body.classList.remove("dashboards");
    };
  }, []);

  // When Chrome switches to print mode for PDF generation the sidebar gets display:none,
  // making the grid container wider. SizedGridLayout (WidthProvider) only re-measures on
  // window.resize, which Chrome doesn't fire automatically when entering print mode.
  // Dispatching a resize event on beforeprint causes WidthProvider to re-measure the
  // correct print-mode width before the PDF is rendered.
  React.useEffect(() => {
    if (!isScheduledReportMode) return;
    const mql = window.matchMedia("print");
    const triggerResize = () => window.dispatchEvent(new Event("resize"));
    mql.addEventListener("change", triggerResize);
    return () => mql.removeEventListener("change", triggerResize);
  }, [isScheduledReportMode]);

  React.useEffect(() => {
    loadAndSetDashboards();
    loadScheduledReports();
  }, [loadAndSetDashboards, loadScheduledReports]);

  const defaultParentConfig = React.useMemo(() => DashboardUtils.getDefaultParentConfig(), []);

  const [currentDashboard, setCurrentDashboard] = React.useState(null);
  const [latestDashboardConfig, setLatestDashboardConfig] = React.useState(null);
  const [filtersExpanded, setFiltersExpanded] = React.useState(false);
  const [originalDashboard, setOriginalDashboard] = React.useState(null);
  const [dashboardHasUnsavedChanges, setDashboardHasUnsavedChanges] = React.useState(null);
  const [activeTabId, setActiveTabId] = React.useState(null);
  const activeTabIdRef = React.useRef(activeTabId);
  React.useEffect(() => { activeTabIdRef.current = activeTabId; }, [activeTabId]);
  const currentDashboardConfig = currentDashboard && currentDashboard.getIn(["json", "dataConfig"], Immutable.Map());

  // Some components require further loading after they render so Rata.isLoaded(data) is insufficient to tell if the
  // dashboard has finished loading
  const loadingRef = React.useRef(Immutable.Set());
  const resetLoadTracking = React.useCallback(() => {
    loadingRef.current = Immutable.Set();
  }, []);
  const onComponentLoadStart = React.useCallback(id => {
    loadingRef.current = loadingRef.current.add(id);
  }, []);
  const onComponentLoadComplete = React.useCallback(id => {
    loadingRef.current = loadingRef.current.delete(id);
    if (loadingRef.current.isEmpty()) {
      // Indicates to browserless load has completed for scheduled reports
      document.dispatchEvent(new Event("CUBE19-dashboard-load-complete"));
    }
  }, []);

  React.useEffect(() => {
    if (currentDashboardConfig) {
      setLatestDashboardConfig(currentDashboardConfig);
    }
  }, [currentDashboardConfig]);

  const parentConfigHasChanged = React.useMemo(() => {
    // NOTE we have an issue with set => list comparisons.
    // There's a workaround in client-filter that can be improved upon but not urgent
    if (currentDashboardConfig && latestDashboardConfig) {
      return !Immutable.is(latestDashboardConfig, currentDashboardConfig);
    } else {
      return false;
    }
  }, [latestDashboardConfig, currentDashboardConfig]);

  React.useEffect(() => {
    if (currentDashboard && originalDashboard) {
      // Cheap checks: run immediately so tab deletion / rename always shows the dot
      const nameChanged = currentDashboard.get("name") !== originalDashboard.get("name");
      const tabsChanged = !Immutable.is(
          currentDashboard.getIn(["json", "tabs"]),
          originalDashboard.getIn(["json", "tabs"])
      );

      if (nameChanged || tabsChanged) {
        setDashboardHasUnsavedChanges(true);
        return;
      }

      // Expensive check: debounce to avoid thrashing during data loads / ag-grid callbacks
      const currentComponents = currentDashboard.getIn(["json", "components"]);
      const originalComponents = originalDashboard.getIn(["json", "components"]);
      const timer = setTimeout(() => {
        // Compare components, but handle columnStates specially
        // ag-grid may filter columnStates to only include active columns, which is not a real change
        const componentsAreEqual = (current, original) => {
          if (!current || !original) {
            return current === original;
          }
          if (current.size !== original.size) {
            return false;
          }

          return current.every((currentComp, index) => {
            const originalComp = original.get(index);
            if (!originalComp) {
              return false;
            }

            const currentWithoutColStates = currentComp.deleteIn(["dataConfig", "columnStates"]);
            const originalWithoutColStates = originalComp.deleteIn(["dataConfig", "columnStates"]);

            // Use _.isEqual for deep comparison
            // Immutable.is was causing false positives when the actual data is identical
            if (!_.isEqual(currentWithoutColStates.toJS(), originalWithoutColStates.toJS())) {
              return false;
            }

            const currentColStates = currentComp.getIn(["dataConfig", "columnStates"], Immutable.List());
            const originalColStates = originalComp.getIn(["dataConfig", "columnStates"], Immutable.List());

            if (Immutable.is(currentColStates, originalColStates)) {
              return true;
            }

            if (currentColStates.size !== originalColStates.size) {
              return false;
            }

            // ag-grid may filter columnStates to only include active columns on render, which is
            // not a real change. Column reorders are tracked via the separate `columnOrder` config
            // key (set by handleColumnMoved and compared by the generic _.isEqual path above). We
            // do NOT check colId sequence here because ag-grid can reorder `columnStates` during
            // initialisation (e.g. after setPivotResultColumns) without any user action, which
            // would produce false-positive "unsaved changes" indicators.
            const originalByColId = originalColStates.reduce((acc, col) =>
                acc.set(col.get("colId"), col), Immutable.Map());

            const currentIsSubsetOrSame = currentColStates.every(currentCol => {
              const originalCol = originalByColId.get(currentCol.get("colId"));
              if (!originalCol) {
                return false;
              }
              const relevantKeys = ["colId", "rowGroup", "pivot", "aggFunc", "hide", "pinned"];
              return relevantKeys.every(key =>
                  Immutable.is(currentCol.get(key), originalCol.get(key))
              );
            });

            return currentIsSubsetOrSame;
          });
        };

        setDashboardHasUnsavedChanges(!componentsAreEqual(currentComponents, originalComponents));
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setDashboardHasUnsavedChanges(false);
    }
  }, [currentDashboard, originalDashboard]);

  const queueRef = React.useRef(null);
  const queueValue = DashboardUtils.useDataLoadingQueue();
  queueRef.current = queueValue;
  const {queue: queueDataLoad, clear: clearDataLoad} = queueValue;
  React.useEffect(() => () => clearDataLoad(), [clearDataLoad]);

  const dashboardWithLatestConfig = React.useMemo(() => {
    if (latestDashboardConfig && currentDashboard) {
      return DashboardUtils.applyParentConfigToDashboard(
          currentDashboard,
          latestDashboardConfig,
          componentTypeToTemplate
      );
    } else {
      return currentDashboard;
    }
  }, [currentDashboard, latestDashboardConfig]);

  const tabIdToCacheEntryRef = React.useRef(Immutable.Map());
  const previousActiveTabIdRef = React.useRef(null);
  // Tab currently being prefetched in the background (one tab ahead of active).
  // Tracked so it can be pinned against budget eviction and to repoint on tab switch.
  const preloadTabIdRef = React.useRef(null);

  // Cache lives in a ref so mutations don't render. Bump this whenever the ref
  // is reassigned so cached-tab-derived memos (cachedTabIds / idToDataByTab /
  // uiStateByTab) recompute — e.g. on add, evict, or budget enforcement.
  const [cacheVersion, setCacheVersion] = React.useState(0);
  const commitCache = React.useCallback(newCache => {
    tabIdToCacheEntryRef.current = newCache;
    setCacheVersion(v => v + 1);
  }, []);

  const handleDataChange = React.useCallback((tabId, dataId, wrapper, weight) => {
    const cache = tabIdToCacheEntryRef.current;
    const updated = TabCache.setComponentData(cache, tabId, dataId, wrapper, weight);
    const pinnedTabIds = [activeTabIdRef.current, previousActiveTabIdRef.current, preloadTabIdRef.current].filter(x => x != null);
    commitCache(TabCache.enforceBudget(updated, {weightBudget: TabCache.WEIGHT_BUDGET, pinnedTabIds}));
  }, [commitCache]);

  const loadDataForActiveTab = React.useCallback(
      (dashboard, tabId) => {
        resetLoadTracking();
        const cache = tabIdToCacheEntryRef.current;
        const withEntry = cache.has(tabId)
            ? cache
            : cache.set(tabId, TabCache.createEmptyEntry({loadStatus: TabCache.TabLoadStatus.LOADING}));
        commitCache(TabCache.markActive(withEntry, tabId, Date.now()));
        DashboardUtils.loadDataForTab(
            componentIdToLoadIdRef,
            dashboard,
            tabId,
            queueDataLoad,
            handleDataChange,
            componentTypeToTemplate,
            getPanelWeight,
            onComponentLoadStart,
            onComponentLoadComplete
        );
        // If no components required async loading (e.g. all Text panels, or empty first tab)
        // then loadingRef is already empty and onComponentLoadComplete will never be called.
        // Fire the completion event now so browserless doesn't time out.
        if (loadingRef.current.isEmpty()) {
          document.dispatchEvent(new Event("CUBE19-dashboard-load-complete"));
        }
        const afterLoadCache = tabIdToCacheEntryRef.current;
        if (afterLoadCache.has(tabId) && afterLoadCache.getIn([tabId, "idToData"], Immutable.Map()).isEmpty()) {
          commitCache(afterLoadCache.setIn([tabId, "loadStatus"], TabCache.TabLoadStatus.LOADED));
        }
      },
      [queueDataLoad, onComponentLoadStart, onComponentLoadComplete, resetLoadTracking, handleDataChange, commitCache]
  );

  // Background prefetch of a non-active tab. Reuses loadDataForTab but deliberately
  // omits onComponentLoadStart/onComponentLoadComplete and resetLoadTracking so the
  // background load never touches loadingRef or the CUBE19-dashboard-load-complete
  // event (those track the active tab for browserless/scheduled reports).
  const preloadTab = React.useCallback(
      (dashboard, tabId) => {
        if (tabId == null || tabId === activeTabIdRef.current) return;
        const cache = tabIdToCacheEntryRef.current;
        if (cache.has(tabId)) return; // already loading or loaded
        commitCache(cache.set(tabId, TabCache.createEmptyEntry({loadStatus: TabCache.TabLoadStatus.LOADING})));
        preloadTabIdRef.current = tabId;
        DashboardUtils.loadDataForTab(
            componentIdToLoadIdRef,
            dashboard,
            tabId,
            queueDataLoad,
            handleDataChange,
            componentTypeToTemplate,
            getPanelWeight
        );
      },
      [queueDataLoad, handleDataChange, commitCache]
  );

  // Once the active tab is fully loaded, prefetch the next tab (by position, wrapping
  // to the first tab) in the background. Keyed on the ACTIVE tab's status so only one
  // tab ahead is ever prefetched; the cache.has guard inside preloadTab makes this
  // idempotent against the cacheVersion bumps it triggers.
  React.useEffect(() => {
    if (!currentDashboard || activeTabId == null) return;
    const status = TabCache.getLoadStatus(tabIdToCacheEntryRef.current, activeTabId);
    if (status !== TabCache.TabLoadStatus.LOADED) return;
    const nextTabId = DashboardUtils.getNextTabInOrder(currentDashboard, activeTabId);
    preloadTab(currentDashboard, nextTabId);
  }, [activeTabId, cacheVersion, currentDashboard, preloadTab]);

  const switchToDashboard = React.useCallback(
      (dashboard, ownershipType) => {
        // NOTE must update timeframe to ensure correct start/end dates

        const parentConfigFromDatabase = dashboard
            .getIn(["json", "dataConfig"], Immutable.Map())
            .update("timeframe", defaultParentConfig.get("timeframe"), (timeframe) =>
                Immutable.fromJS(TimeframeRepo.parse(timeframe.toJS()).getRawJson())
            );
        const parentConfigForDashboard = defaultParentConfig.merge(parentConfigFromDatabase);
        dashboard = DashboardUtils.applyParentConfigToDashboard(
            dashboard,
            parentConfigForDashboard,
            componentTypeToTemplate
        );

        // Remove deleted tags
        const validTagIds = Immutable.fromJS(TagRepo.getAll().toJSON())
            .map((tag) => tag.get("id"))
            .toSet();
        const removeDeletedTagsFromTagFilter = (tagFilter) => {
          if (!tagFilter) {
            return tagFilter;
          }
          return tagFilter
              .update("matchAllTagIds", Immutable.List(), (ids) => ids.filter((id) => validTagIds.contains(id)))
              .update("matchAnyTagIds", Immutable.List(), (ids) => ids.filter((id) => validTagIds.contains(id)))
              .update("excludedTagIds", Immutable.List(), (ids) => ids.filter((id) => validTagIds.contains(id)));
        };

        const normalizeChartConfig = (dataConfig) => {
          if (!dataConfig) {
            return dataConfig;
          }
          const chart = dataConfig.get("chart");
          if (chart && !chart.get("type")) {
            return dataConfig.setIn(["chart", "type"], "stackedColumn");
          }
          return dataConfig;
        };

        // Normalize columnStates to have all properties with default values
        // This prevents false unsaved changes when ag-grid expands minimal saved state
        // NOTE: sortType is intentionally NOT in defaults - ag-grid only includes it when sorting is applied
        const columnStateDefaults = Immutable.Map({
          pivot: false,
          hide: true,
          rowGroup: false,
          aggFunc: null,
          width: 200,
          rowGroupIndex: null,
          pivotIndex: null,
          sortIndex: null,
          pinned: null,
          flex: null,
          sort: null
        });

        const normalizeColumnStates = (dataConfig) => {
          if (!dataConfig) {
            return dataConfig;
          }
          const columnStates = dataConfig.get("columnStates");
          if (columnStates && Immutable.List.isList(columnStates)) {
            // Get colOrder to apply sort info
            const colOrder = dataConfig.get("colOrder", Immutable.List());
            const colIdToSortInfo = colOrder.reduce((acc, sortCol) => {
              const colId = sortCol.get ? sortCol.get("colId") : sortCol.colId;
              return acc.set(colId, sortCol);
            }, Immutable.Map());

            let rowGroupIndex = 0;
            let pivotIndex = 0;

            const normalizedStates = columnStates.map(state => {
              let normalized = columnStateDefaults.merge(state);
              const colId = normalized.get("colId");

              // Remove sortType if it exists but is null (ag-grid doesn't include it when not sorting)
              if (normalized.has("sortType") && normalized.get("sortType") === null) {
                normalized = normalized.delete("sortType");
              }

              if (normalized.get("rowGroup")) {
                normalized = normalized.set("rowGroupIndex", rowGroupIndex++);
              }

              if (normalized.get("pivot")) {
                normalized = normalized.set("pivotIndex", pivotIndex++);
              }

              const sortInfo = colIdToSortInfo.get(colId);
              if (sortInfo) {
                const sort = sortInfo.get ? sortInfo.get("sort") : sortInfo.sort;
                const sortIndex = sortInfo.get ? sortInfo.get("sortIndex") : sortInfo.sortIndex;
                if (sort) {
                  normalized = normalized.set("sort", sort);
                  normalized = normalized.set("sortIndex", sortIndex !== undefined ? sortIndex : 0);
                  // ag-grid sets sortType to "default" when sorting is applied
                  normalized = normalized.set("sortType", "default");
                }
              }

              return normalized;
            });
            return dataConfig.set("columnStates", normalizedStates);
          }
          return dataConfig;
        };

        dashboard = dashboard
            .updateIn(["json", "dataConfig", "tagFilter"], removeDeletedTagsFromTagFilter)
            .updateIn(["json", "components"], Immutable.List(), (components) =>
                components.map((component) => component
                    .updateIn(["dataConfig", "tagFilter"], removeDeletedTagsFromTagFilter)
                    .update("dataConfig", normalizeChartConfig)
                    .update("dataConfig", normalizeColumnStates))
            );


        const firstTab = dashboard.getIn(["json", "tabs"], Immutable.List())
            .sortBy(t => t.get("position"))
            .first();
        const firstTabId = firstTab ? firstTab.get("id") : null;
        // In scheduled report mode we only care about the first tab, so strip everything else
        if (isScheduledReportMode && firstTabId !== null) {
          dashboard = dashboard.updateIn(["json", "components"], Immutable.List(), components =>
              components.filter(c => c.get("tabId") === firstTabId)
          );
        }
        commitCache(Immutable.Map());
        previousActiveTabIdRef.current = null;
        clearDataLoad();
        loadDataForActiveTab(dashboard, firstTabId);
        setCurrentDashboard(dashboard);
        setLatestDashboardConfig(dashboard.getIn(["json", "dataConfig"]));
        setOriginalDashboard(dashboard);
        setActiveTabId(firstTabId);
        setEditing(false);

        Auditor.uniqueAudit("dashboard:loaded", {
          id: dashboard.get("id"),
          name: dashboard.get("name"),
          panelCount: dashboard.getIn(["json", "components"]).count(),
          panels: dashboard.getIn(["json", "components"]).map((c) => c.get("type"))
        });
        const dashboardType = dashboardHasUnsavedChanges ? "custom" : ownershipType;
        // eslint-disable-next-line default-case
        switch (dashboardType) {
          case "custom":
            Auditor.audit("dashboards:run-custom-report", {
              dashboardId: dashboard.get("id"),
              dashboardName: dashboard.get("name")
            });
            break;
          case "ownedConfigs":
            Auditor.audit("dashboards:run-saved-report", {
              dashboardId: dashboard.get("id"),
              dashboardName: dashboard.get("name")
            });
            break;
          case "sharedUserConfigs":
          case "sharedGroupConfigs":
            Auditor.audit("dashboards:run-shared-report", {
              dashboardId: dashboard.get("id"),
              dashboardName: dashboard.get("name")
            });
        }
      },
      [defaultParentConfig, loadDataForActiveTab, clearDataLoad, dashboardHasUnsavedChanges, isScheduledReportMode, commitCache]
  );

  const dashboardData = React.useMemo(() => {
    if (!Rata.isLoaded(ownershipTypeToDashboardsWrapper)) {
      return {
        allDashboards: Immutable.List(),
        idToDashboard: Immutable.Map(),
        ownershipTypeToDashboards: null
      };
    }

    const ownershipTypeToDashboards = Rata.getValue(ownershipTypeToDashboardsWrapper);
    const allDashboards = ownershipTypeToDashboards
        .get("ownedConfigs", Immutable.List())
        .concat(ownershipTypeToDashboards.get("sharedUserConfigs", Immutable.List()))
        .concat(ownershipTypeToDashboards.get("sharedGroupConfigs", Immutable.List()));
    const idToDashboard = indexBy((x) => x.get("id"), allDashboards);

    return {
      allDashboards,
      idToDashboard,
      ownershipTypeToDashboards
    };
  }, [ownershipTypeToDashboardsWrapper]);

  const {allDashboards, idToDashboard, ownershipTypeToDashboards} = dashboardData;

  React.useEffect(() => {
    if (ownershipTypeToDashboards && latestDashboardConfig === null) {
      const queryStringDashboardId = currentQueryString.dashboardid;
      if (queryStringDashboardId) {
        // Never show a different dashboard if an id is specified in the query string. Display a blank one if necessary
        const queryStringDashboard = queryStringDashboardId && idToDashboard.get(parseInt(queryStringDashboardId));
        if (queryStringDashboard) {
          const ownershipType = ownershipTypeToDashboards.findKey((value) => value.includes(queryStringDashboard));
          switchToDashboard(queryStringDashboard, ownershipType);
        } else {
          switchToDashboard(DashboardUtils.createBlankDashboard(defaultParentConfig), "custom");
        }
      } else {
        const homeDashboardId = SavedConfigs.getHome("DASHBOARD");
        const homeDashboard = idToDashboard.get(homeDashboardId);
        if (homeDashboard) {
          const ownershipType = ownershipTypeToDashboards.findKey((value) => value.includes(homeDashboard));
          switchToDashboard(homeDashboard, ownershipType);
        } else {
          // TODO if user can see no dashboards AND doesn't have full editor then maybe show a message explaining that
          if (allDashboards.isEmpty()) {
            switchToDashboard(DashboardUtils.createBlankDashboard(defaultParentConfig), "custom");
          } else {
            const ownershipType = ownershipTypeToDashboards.findKey((value) => value.includes(allDashboards.first()));
            switchToDashboard(allDashboards.first(), ownershipType);
          }
        }
      }
    }
  }, [
    switchToDashboard,
    latestDashboardConfig,
    ownershipTypeToDashboardsWrapper,
    defaultParentConfig,
    idToDashboard,
    allDashboards,
    ownershipTypeToDashboards
  ]);

  // Recompute when the active tab changes OR the cache version bumps (tab
  // added / evicted / budget-enforced). cacheVersion is read so the linter
  // and React both treat it as a dependency.
  const cachedTabIds = React.useMemo(
      () => TabCache.getCachedTabIds(tabIdToCacheEntryRef.current),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [cacheVersion]
  );

  const idToDataByTab = React.useMemo(
      () => cachedTabIds.reduce(
          (m, tabId) => m.set(tabId, TabCache.getActiveIdToData(tabIdToCacheEntryRef.current, tabId)),
          Immutable.Map()),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [cachedTabIds, cacheVersion]
  );

  const uiStateByTab = React.useMemo(
      () => cachedTabIds.reduce(
          (m, tabId) => m.set(tabId, tabIdToCacheEntryRef.current.getIn([tabId, "uiState"], Immutable.Map())),
          Immutable.Map()),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [cachedTabIds, cacheVersion]
  );

  const tabIdToLoadStatus = React.useMemo(() => {
    if (!currentDashboard) return Immutable.Map();
    return currentDashboard.getIn(["json", "tabs"], Immutable.List()).reduce(
        (m, tab) => m.set(tab.get("id"), TabCache.getLoadStatus(tabIdToCacheEntryRef.current, tab.get("id"))),
        Immutable.Map());
    // Load status lives in the cache ref, which mutates as data settles. Recompute
    // on cacheVersion (bumped by commitCache on every data change) so tab spinners
    // clear when loading finishes and update immediately on tab switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDashboard, cacheVersion]);

  const {flag: showComponentMenu, setTrue: openComponentMenu, setFalse: closeComponentMenu} = useBooleanState();
  const [showAiAssistant, setShowAiAssistant] = React.useState(false);
  const [scheduleConfigId, setScheduleConfigId] = React.useState(null);
  const [scheduleIdToEdit, setScheduleIdToEdit] = React.useState(null);

  const [componentInfoToEdit, setComponentInfoToEdit] = React.useState(null);
  const closeEditComponentDrawer = React.useCallback(() => setComponentInfoToEdit(null), []);

  const openAiAssistant = React.useCallback(() => {
    setShowAiAssistant(true);
    setEditing(true);
  }, []);
  const closeAiAssistant = React.useCallback(() => setShowAiAssistant(false), []);

  const handleAddComponent = React.useCallback(
      (newComponent) => {
        closeComponentMenu();

        const template = componentTypeToTemplate.get(newComponent.get("type"));

        const components = currentDashboard.getIn(["json", "components"], Immutable.List());
        const maxId = components.map((c) => c.get("id")).max() || 0;
        const templateDimensions = template.getIn(["layout", "init"]);

        const tabComponents = DashboardUtils.getComponentsForTab(components, activeTabId);
        const bottomOfLastComponent =
            tabComponents.map((c) => c.getIn(["layout", "y"]) + c.getIn(["layout", "height"])).max() || 0;

        newComponent = newComponent
            .set("id", maxId + 1)
            .set("tabId", activeTabId)
            .set("layout", templateDimensions.set("x", 0).set("y", Math.ceil(bottomOfLastComponent)));

        const applyParent = template.get("applyParentConfig");
        newComponent = newComponent.update("dataConfig", (dataConfig) =>
            applyParent(currentDashboard.getIn(["json", "dataConfig"]), dataConfig)
        );

        setComponentInfoToEdit({newComponent});
      },
      [currentDashboard, closeComponentMenu, activeTabId]
  );

  const [renderDialogs, openDialog] = useDialogStack();
  const [ref, {width}] = useDimensions();
  const toolbarRef = React.useRef(null);
  const [toolbarBottomY, setToolbarBottomY] = React.useState(112);
  React.useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const update = () => setToolbarBottomY(el.getBoundingClientRect().bottom);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const switchToBlankDashboard = React.useCallback(() => {
    switchToDashboard(DashboardUtils.createBlankDashboard(defaultParentConfig), "custom");
    setMessagesToDashboardsWrapper((wrapper) =>
        Rata.updateValue(wrapper, (messagesMap) => messagesMap.delete(undefined))
    );
  }, [switchToDashboard, defaultParentConfig]);

  const reloadCurrentDashboard = React.useCallback(() => {
    const newDashboard = DashboardUtils.applyParentConfigToDashboard(
        currentDashboard,
        latestDashboardConfig,
        componentTypeToTemplate
    );
    setCurrentDashboard(newDashboard);

    if (dashboardHasUnsavedChanges) {
      Auditor.audit("dashboards:run-custom-report", {});
    }

    loadDataForActiveTab(newDashboard, activeTabIdRef.current);
  }, [
    currentDashboard, latestDashboardConfig, dashboardHasUnsavedChanges,
    loadDataForActiveTab
  ]);

  const handleTabChange = React.useCallback((newTabId) => {
    const oldTabId = activeTabIdRef.current;
    if (newTabId === oldTabId) return;

    if (oldTabId != null) {
      const snapshots = captureUiStateRefs.current
          .map(captureFn => {
            try { return captureFn(); } catch (e) { return null; }
          })
          .filter(s => s != null);
      let cache = tabIdToCacheEntryRef.current;
      commitCache(snapshots.reduce(
          (c, state, componentId) => TabCache.setUiState(c, oldTabId, componentId, state),
          cache));
    }

    previousActiveTabIdRef.current = oldTabId;

    const hadEntry = tabIdToCacheEntryRef.current.has(newTabId);

    const cache = tabIdToCacheEntryRef.current;
    if (cache.has(newTabId)) {
      commitCache(TabCache.markActive(cache, newTabId, Date.now()));
    } else {
      commitCache(TabCache.markActive(
          cache.set(newTabId, TabCache.createEmptyEntry({loadStatus: TabCache.TabLoadStatus.LOADING})),
          newTabId,
          Date.now()
      ));
    }

    if (currentDashboard && !hadEntry) {
      DashboardUtils.loadDataForTab(
          componentIdToLoadIdRef,
          currentDashboard,
          newTabId,
          queueDataLoad,
          handleDataChange,
          componentTypeToTemplate,
          getPanelWeight,
          onComponentLoadStart,
          onComponentLoadComplete
      );
      const afterTabCache = tabIdToCacheEntryRef.current;
      if (afterTabCache.has(newTabId) && afterTabCache.getIn([newTabId, "idToData"], Immutable.Map()).isEmpty()) {
        commitCache(afterTabCache.setIn([newTabId, "loadStatus"], TabCache.TabLoadStatus.LOADED));
      }
    }

    queueRef.current.prioritiseTab(newTabId);
    setActiveTabId(newTabId);
  }, [currentDashboard, queueDataLoad, handleDataChange,
    onComponentLoadStart, onComponentLoadComplete, commitCache]);

  const handleLoadLatestData = React.useCallback(() => {
    const tabsToEvict = tabIdToCacheEntryRef.current
        .keySeq()
        .filter(tabId => tabId !== activeTabIdRef.current)
        .toList();
    commitCache(tabsToEvict.reduce((c, tabId) => TabCache.evictTab(c, tabId), tabIdToCacheEntryRef.current));
    tabsToEvict.forEach(tabId => queueRef.current.dropForTab(tabId));
    reloadCurrentDashboard();
  }, [reloadCurrentDashboard, commitCache]);

  const afterDashboardDeleted = React.useCallback(
      (dashboardId) => {
        const name = Rata.getValue(ownershipTypeToDashboardsWrapper)
            .get("ownedConfigs")
            .find((db) => db.get("id") === dashboardId)
            .get("name");
        Auditor.audit("dashboards:delete-report", {name});
        setCurrentDashboard((d) => {
          if (d.get("id") === dashboardId) {
            return d.delete("id");
          } else {
            return d;
          }
        });
      },
      [ownershipTypeToDashboardsWrapper]
  );

  const handleNameChange = React.useCallback(
      (name) => {
        setCurrentDashboard(currentDashboard.set("name", name));
      },
      [currentDashboard]
  );

  const handleDashboardSave = React.useCallback((savedDashboard) => {
    setOriginalDashboard(savedDashboard);
  }, []);

  const handleDashboardSaveAs = React.useCallback(
      (savedDashboard) => {
        const originalDashboardId = originalDashboard.get("id");
        const newDashboardId = savedDashboard.get("id");
        setMessagesToDashboardsWrapper((wrapper) =>
            Rata.updateValue(wrapper, (messagesMap) =>
                messagesMap.set(newDashboardId, messagesMap.get(originalDashboardId, Immutable.List()))
            )
        );
        setOriginalDashboard(savedDashboard);
      },
      [originalDashboard]
  );

  const handleDashboardChange = React.useCallback(
      (newDashboardOrFn, {reloadComponentId = null, reloadDashboard = false, scrollToComponent = false} = {}) => {
        setCurrentDashboard((oldDashboard) => {
          let newDashboard;
          if (typeof newDashboardOrFn === "function") {
            newDashboard = newDashboardOrFn(oldDashboard);
          } else {
            newDashboard = newDashboardOrFn;
          }
          newDashboard = DashboardUtils.applyParentConfigToDashboard(
              newDashboard,
              newDashboard.getIn(["json", "dataConfig"]),
              componentTypeToTemplate
          );

          if (reloadDashboard) {
            loadDataForActiveTab(newDashboard, activeTabIdRef.current);
          } else if (reloadComponentId) {
            DashboardUtils.loadDataForComponent(
                reloadComponentId,
                componentIdToLoadIdRef,
                newDashboard,
                queueDataLoad,
                handleDataChange,
                componentTypeToTemplate,
                getPanelWeight
            );
          }

          if (scrollToComponent && reloadComponentId && scrollToComponentRef.current) {
            scrollToComponentRef.current(reloadComponentId);
          }

          return newDashboard;
        });
      },
      [componentIdToLoadIdRef, loadDataForActiveTab, queueDataLoad, handleDataChange]
  );

  const handleReloadComponent = React.useCallback(
      (componentId) => {
        return DashboardUtils.loadDataForComponent(
            componentId,
            componentIdToLoadIdRef,
            currentDashboard,
            queueDataLoad,
            handleDataChange,
            componentTypeToTemplate,
            getPanelWeight
        );
      },
      [currentDashboard, queueDataLoad, handleDataChange]
  );

  const handleDuplicateComponent = React.useCallback(
      (componentId) => {
        const components = currentDashboard.getIn(["json", "components"], Immutable.List());
        const componentIndex = components.findIndex((c) => c.get("id") === componentId);
        let component = currentDashboard.getIn(["json", "components", componentIndex]);
        const template = componentTypeToTemplate.get(component.get("type"));
        const title = template.get("getTitle")(component);

        const maxId = components.map((c) => c.get("id")).max() || 0;
        const newId = maxId + 1;
        const bottomOfLastComponent =
            components.map((c) => c.getIn(["layout", "y"]) + c.getIn(["layout", "height"])).max() || 0;
        const newComponent = component
            .set("id", newId)
            .set("fullScreen", false)
            .update("layout", (layout) => layout.set("x", 0).set("y", bottomOfLastComponent))
            .update("title", () => title && title + " copy");

        const initialData = DashboardUtils.getInitialComponentData(newComponent, componentTypeToTemplate);
        const initialDataId = DashboardUtils.getDataId(componentIdToLoadIdRef.current, newComponent);
        handleDataChange(activeTabId, initialDataId, initialData, 0);
        handleDashboardChange(
            (currentDashboard) => {
              return currentDashboard
                  .updateIn(["json", "components"], Immutable.List(), (components) => components.push(newComponent))
                  .updateIn(["json", "components", componentIndex], (component) => component.set("fullScreen", false));
            },
            {reloadComponentId: newId}
        );
      },
      [currentDashboard, handleDashboardChange, activeTabId, handleDataChange]
  );

  const handleAddTab = React.useCallback(() => {
    const tabs = currentDashboard.getIn(["json", "tabs"], Immutable.List());
    const newId = DashboardUtils.getNextTabId(tabs);
    const existingNames = new Set(tabs.map(t => t.get("name").toLowerCase()).toArray());
    let tabNumber = tabs.size + 1;
    while (existingNames.has(("Tab " + tabNumber).toLowerCase())) {
      tabNumber++;
    }
    const newTab = Immutable.fromJS({
      id: newId,
      name: "Tab " + tabNumber,
      position: tabs.size
    });
    setCurrentDashboard(currentDashboard.updateIn(["json", "tabs"], Immutable.List(), ts => ts.push(newTab)));

    const oldTabId = activeTabIdRef.current;
    if (oldTabId != null) {
      const snapshots = captureUiStateRefs.current
          .map(captureFn => {
            try { return captureFn(); } catch (e) { return null; }
          })
          .filter(s => s != null);
      let cache = tabIdToCacheEntryRef.current;
      commitCache(snapshots.reduce(
          (c, state, componentId) => TabCache.setUiState(c, oldTabId, componentId, state),
          cache));
    }
    previousActiveTabIdRef.current = oldTabId;

    const cache = tabIdToCacheEntryRef.current;
    commitCache(TabCache.markActive(
        cache.set(newId, TabCache.createEmptyEntry({loadStatus: TabCache.TabLoadStatus.LOADED})),
        newId,
        Date.now()
    ));
    setActiveTabId(newId);
  }, [currentDashboard, commitCache]);

  const handleRenameTab = React.useCallback((tabId, newName) => {
    setCurrentDashboard(dashboard => {
      const tabs = dashboard.getIn(["json", "tabs"], Immutable.List());
      const index = tabs.findIndex(t => t.get("id") === tabId);
      return dashboard.setIn(["json", "tabs", index, "name"], newName);
    });
  }, []);

  const handleDeleteTab = React.useCallback((tabId) => {
    const tabs = currentDashboard.getIn(["json", "tabs"], Immutable.List());
    const components = currentDashboard.getIn(["json", "components"], Immutable.List());

    if (tabs.size <= 1) {
      const newId = DashboardUtils.getNextTabId(tabs);
      const freshTab = Immutable.fromJS({id: newId, name: "Tab 1", position: 0});
      queueRef.current.dropForTab(tabId);
      commitCache(TabCache.evictTab(tabIdToCacheEntryRef.current, tabId));
      setCurrentDashboard(currentDashboard
          .setIn(["json", "components"], Immutable.List())
          .setIn(["json", "tabs"], Immutable.List([freshTab])));
      setActiveTabId(newId);
      return;
    }

    const tabIndex = tabs.findIndex(t => t.get("id") === tabId);
    const remainingTabs = DashboardUtils.normalizeTabPositions(tabs.delete(tabIndex));
    const remainingComponents = components.filter(c => c.get("tabId") !== tabId);

    if (tabId === activeTabId) {
      const sortedTabs = tabs.sortBy(t => t.get("position"));
      const deletedPosition = sortedTabs.findIndex(t => t.get("id") === tabId);
      const newActivePosIndex = deletedPosition > 0 ? deletedPosition - 1 : 0;
      const sortedRemaining = remainingTabs.sortBy(t => t.get("position"));
      const newActiveTab = sortedRemaining.get(Math.min(newActivePosIndex, sortedRemaining.size - 1));
      setActiveTabId(newActiveTab.get("id"));
    }

    queueRef.current.dropForTab(tabId);
    commitCache(TabCache.evictTab(tabIdToCacheEntryRef.current, tabId));
    if (previousActiveTabIdRef.current === tabId) {
      previousActiveTabIdRef.current = null;
    }

    setCurrentDashboard(currentDashboard
        .setIn(["json", "components"], remainingComponents)
        .setIn(["json", "tabs"], remainingTabs));
  }, [currentDashboard, activeTabId, commitCache]);

  const handleCloneTab = React.useCallback((tabId) => {
    const tabs = currentDashboard.getIn(["json", "tabs"], Immutable.List());
    const components = currentDashboard.getIn(["json", "components"], Immutable.List());
    const sourceTab = tabs.find(t => t.get("id") === tabId);
    const sourceComponents = components.filter(c => c.get("tabId") === tabId);

    const newTabId = DashboardUtils.getNextTabId(tabs);
    const sourcePosition = sourceTab.get("position");

    const shiftedTabs = tabs.map(t =>
        t.get("position") > sourcePosition
            ? t.update("position", p => p + 1)
            : t
    );

    const existingTabNames = tabs.map(t => t.get("name"));
    const newTab = Immutable.fromJS({
      id: newTabId,
      name: getUniqueName(sourceTab.get("name"), existingTabNames),
      position: sourcePosition + 1
    });

    let nextComponentId = DashboardUtils.getNextComponentId(components);
    const clonedComponents = sourceComponents.map(c => {
      const newComponent = c.set("id", nextComponentId).set("tabId", newTabId);
      nextComponentId++;
      return newComponent;
    });

    const newDashboard = currentDashboard
        .setIn(["json", "tabs"], DashboardUtils.normalizeTabPositions(shiftedTabs.push(newTab)))
        .updateIn(["json", "components"], cs => cs.concat(clonedComponents));

    setCurrentDashboard(newDashboard);
    setActiveTabId(newTabId);

    const cache = tabIdToCacheEntryRef.current;
    commitCache(TabCache.markActive(
        cache.set(newTabId, TabCache.createEmptyEntry({loadStatus: TabCache.TabLoadStatus.LOADING})),
        newTabId,
        Date.now()
    ));

    DashboardUtils.loadDataForTab(
        componentIdToLoadIdRef,
        newDashboard,
        newTabId,
        queueDataLoad,
        handleDataChange,
        componentTypeToTemplate,
        getPanelWeight,
        onComponentLoadStart,
        onComponentLoadComplete
    );
  }, [currentDashboard, queueDataLoad, onComponentLoadStart, onComponentLoadComplete, handleDataChange, commitCache]);

  const handleMoveTab = React.useCallback((tabId, direction) => {
    setCurrentDashboard(dashboard => DashboardUtils.moveTab(dashboard, tabId, direction));
  }, []);

  //fix to prevent background scroll when loading a dashboard saved with a fullscreen component
  //or switching to a non-fullscreen dashboard from a fullscreen one
  React.useEffect(() => {
    const components = currentDashboard
        ? currentDashboard.getIn(["json", "components"], Immutable.List())
        : Immutable.List();
    const hasFullSizedComponent = components.some((c) => c.get("fullScreen"));
    if (hasFullSizedComponent) {
      DashboardUtils.lockPageScroll();
    } else {
      DashboardUtils.enablePageScroll();
    }
  }, [currentDashboard]);

  const currentDashboardId = currentDashboard?.get("id");
  React.useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentDashboardId]);

  // We have a lot of panel types that try to steal focus
  // On load we always want the focus to be the dashboard so it can be scrolled with a keyboard
  React.useEffect(() => {
    if (scrollContainerRef.current) {
      requestAnimationFrame(() => scrollContainerRef.current?.focus());
    }
    const refocusOnBodyFocus = (e) => {
      if (e.relatedTarget === null) {
        scrollContainerRef.current?.focus();
      }
    };
    document.addEventListener("focusout", refocusOnBodyFocus);
    return () => document.removeEventListener("focusout", refocusOnBodyFocus);
  }, [currentDashboardId]);

  React.useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [isSidebarExpanded]);

  const hasNoSharedDashboards = React.useMemo(() => {
    const allDashboards = Rata.getValue(ownershipTypeToDashboardsWrapper);
    return allDashboards.get("sharedUserConfigs")?.isEmpty() && allDashboards.get("sharedGroupConfigs")?.isEmpty();
  }, [ownershipTypeToDashboardsWrapper]);

  const hasPanels = React.useMemo(() => {
    const components = dashboardWithLatestConfig?.getIn(["json", "components"]);
    if (!components) {
      return false;
    }
    const tabs = dashboardWithLatestConfig?.getIn(["json", "tabs"]);
    if (tabs && !tabs.isEmpty()) {
      return components.some(c => c.get("tabId") === activeTabId);
    }
    return !components.isEmpty();
  }, [dashboardWithLatestConfig, activeTabId]);

  const showAiAssistantFallback = React.useMemo(() => {
    return (
        Users.currentHasPermission("DASHBOARD_FULL_EDITOR") &&
        !hasPanels &&
        !isEditing &&
        currentClient.hasPermission(clientPermissions.canUseAiAssistant)
    );
  }, [hasPanels, isEditing]);

  const showFullEditorFallback = React.useMemo(() => {
    return (
        Users.currentHasPermission("DASHBOARD_FULL_EDITOR") &&
        !hasPanels &&
        !isEditing &&
        !currentClient.hasPermission(clientPermissions.canUseAiAssistant)
    );
  }, [hasPanels, isEditing]);

  const showSharedFallback = React.useMemo(() => {
    return !Users.currentHasPermission("DASHBOARD_FULL_EDITOR") && !hasPanels && hasNoSharedDashboards && !isEditing;
  }, [hasPanels, isEditing, hasNoSharedDashboards]);

  const getMessagesForCurrentDashboard = React.useCallback(() => {
    const dashboardId = currentDashboard?.get("id");
    const messagesMap = Rata.getValue(messagesToDashboardsWrapper, Immutable.Map());
    return messagesMap.get(dashboardId, Immutable.List());
  }, [currentDashboard, messagesToDashboardsWrapper]);

  const addMessageToDashboard = (dashboardId, message) => {
    setMessagesToDashboardsWrapper((wrapper) =>
        Rata.updateValue(wrapper, (messagesMap) => {
          const currentMessages = messagesMap.get(dashboardId, Immutable.List());
          const newMessage = Immutable.Map({
            id: Date.now(), // Simple ID for now
            type: message.type,
            content: message.content,
            timestamp: new Date().toISOString(),
            componentConfig: message.componentConfig
          });
          return messagesMap.set(dashboardId, currentMessages.push(newMessage));
        })
    );
  };

  const handleAiAssistantStartOver = React.useCallback(() => {
    const dashboardId = currentDashboard?.get("id");
    setMessagesToDashboardsWrapper((wrapper) =>
        Rata.updateValue(wrapper, (messagesMap) => {
          return messagesMap.delete(dashboardId);
        })
    );
  }, [currentDashboard]);

  const fetchAIAssistantResponse = React.useCallback(
      (latestMessage) => {
        // Add the latest message as it's not yet in getMessagesForCurrentDashboard
        const allMessagesForBackend = getMessagesForCurrentDashboard()
            .push(
                Immutable.Map({
                  type: "user",
                  content: latestMessage
                })
            )
            .map((msg) => ({
              message: msg.get("content"),
              isServerResponse: msg.get("type") === "assistant",
              panelConfig: msg.get("componentConfig")
            }))
            .toArray();

        return ajax
            .post({
              url: "ai-assistant/panel-config",
              json: {
                messages: allMessagesForBackend
              }
            })
            .then((res) => Immutable.fromJS(res));
      },
      [getMessagesForCurrentDashboard]
  );

  const sendMessageToAIAssistant = React.useCallback(
      (message) => {
        const dashboardId = currentDashboard.get("id");
        addMessageToDashboard(dashboardId, {
          type: "user",
          content: message
        });
        setMessagesToDashboardsWrapper((wrapper) => Rata.toLoading(wrapper));

        fetchAIAssistantResponse(message)
            .then((result) => {
              addMessageToDashboard(dashboardId, {
                type: "assistant",
                content: result.get("chatResponse", "Sorry, I encountered an error."),
                componentConfig: result.get("componentConfig")
              });
              setMessagesToDashboardsWrapper((wrapper) => Rata.toLoaded(wrapper, Rata.getValue(wrapper)));
            })
            .catch((error) => {
              setMessagesToDashboardsWrapper((wrapper) => Rata.toError(wrapper, error));
            });
      },
      [currentDashboard, fetchAIAssistantResponse]
  );

  const closeScheduleDrawer = React.useCallback(() => {
    setScheduleConfigId(null);
    setScheduleIdToEdit(null);
  }, []);

  const handleScheduleUpdate = React.useCallback((schedule) => {
    setDashboardIdToScheduledReports((prev) => {
      const dashboardId = schedule.get("dashboardId");
      const scheduleId = schedule.get("id");
      const existingSchedules = prev.get(dashboardId, Immutable.List());
      const existingIndex = existingSchedules.findIndex(
          (s) => s.get("id") === scheduleId
      );

      const updatedSchedules = existingIndex >= 0
          ? existingSchedules.set(existingIndex, schedule)
          : existingSchedules.push(schedule);

      return prev.set(dashboardId, updatedSchedules);
    });
  }, []);

  const handleScheduleDelete = React.useCallback((scheduleId, dashboardId) => {
    setDashboardIdToScheduledReports((prev) => {
      const existingSchedules = prev.get(dashboardId, Immutable.List());
      const updatedSchedules = existingSchedules.filter(schedule => schedule.get("id") !== scheduleId);
      return prev.set(dashboardId, updatedSchedules);
    });
  }, []);

  return (
      <div css={css`
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        @media print {
          height: 100%;
          width: 100%;
          overflow-y: visible;
          height: auto;
        }
      `}>
        <Global styles={globalCss(theme)} />
        <MenuBar appView="dashboards" />

        {isSavingDashboard && <Overlay content="Saving Dashboard..." />}

        <div style={{display: "flex", flex: 1, minHeight: 0}}>
          <DashboardSidebar
              isExpanded={isSidebarExpanded}
              onToggleExpanded={() => setIsSidebarExpanded(prev => !prev)}
              dashboard={dashboardWithLatestConfig}
              onDashboardChange={handleDashboardChange}
              setSavingDashboard={setSavingDashboard}
              handleDashboardSave={handleDashboardSave}
              handleDashboardSaveAs={handleDashboardSaveAs}
              ownershipTypeToDashboardsWrapper={ownershipTypeToDashboardsWrapper}
              setOwnershipTypeToDashboardsWrapper={setOwnershipTypeToDashboardsWrapper}
              isEditing={isEditing}
              setEditing={setEditing}
              hasUnsavedChanges={parentConfigHasChanged || dashboardHasUnsavedChanges}
              onRequestNewDashboard={switchToBlankDashboard}
              onRequestOpenSavedDashboards={openSavedDashboardsDrawer}
              onRequestOpenComponentMenu={openComponentMenu}
          />

          <div ref={ref} style={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", position: "relative"}}>
            <div ref={toolbarRef}>
              <DashboardToolbar
                  dashboard={dashboardWithLatestConfig}
                  isEditing={isEditing}
                  onRequestOpenAiAssistant={openAiAssistant}
                  onRequestOpenComponentMenu={openComponentMenu}
                  handleNameChange={handleNameChange}
                  onRequestReloadDashboard={reloadCurrentDashboard}
                  filtersExpanded={filtersExpanded}
                  onToggleFilters={() => setFiltersExpanded(prev => !prev)}
                  filterConfig={latestDashboardConfig}
              >
              {currentDashboard && (
                  <DataConfigToolbar
                      topLevel
                      expanded={filtersExpanded}
                      allowInherit={false}
                      label="Top level filters"
                      config={latestDashboardConfig}
                      onConfigChange={(config) => setLatestDashboardConfig(config)}
                  />
              )}
            </DashboardToolbar>
            {currentDashboard && currentDashboard.getIn(["json", "tabs"]) && (
                <TabBar
                    tabs={currentDashboard.getIn(["json", "tabs"])}
                    activeTabId={activeTabId}
                    isEditing={isEditing}
                    tabIdToLoadStatus={tabIdToLoadStatus}
                    onTabChange={handleTabChange}
                    onAddTab={handleAddTab}
                    onDeleteTab={handleDeleteTab}
                    onRenameTab={handleRenameTab}
                    onCloneTab={handleCloneTab}
                    onMoveTab={handleMoveTab}
                />
            )}
            </div>

            <div ref={scrollContainerRef} tabIndex={-1} css={css`
              flex: 1;
              overflow: auto;
              position: relative;
              outline: none;
              @media print {
                overflow: visible;
                height: auto;
              }
            `}>
              {currentDashboard ? (
                    <>
                      {showAiAssistantFallback && (
                          <DashboardFallback variant="assistant">
                            <AiAssistantButton
                                onClick={() => {
                                  Auditor.audit("ai-assistant-clicked", {from: "dashboards-empty-state"});
                                  openAiAssistant();
                                }}
                            />
                          </DashboardFallback>
                      )}
                      {showFullEditorFallback && <DashboardFallback variant="create" />}
                      {showSharedFallback && <DashboardFallback variant="share" />}
                      <div>
                        <DashboardDisplay
                            componentIdToLoadId={componentIdToLoadIdRef.current}
                            dashboard={currentDashboard}
                            onDashboardChange={handleDashboardChange}
                            onDuplicateClick={handleDuplicateComponent}
                            isEditing={isEditing}
                            activeTabId={activeTabId}
                            queueDataLoad={queueDataLoad}
                            clearDataLoad={clearDataLoad}
                            onRequestDialog={openDialog}
                            onRequestComponentReload={handleReloadComponent}
                            onRequestEdit={setComponentInfoToEdit}
                            cachedTabIds={cachedTabIds}
                            idToDataByTab={idToDataByTab}
                            uiStateByTab={uiStateByTab}
                            onCaptureUiStateRef={setCaptureUiStateRef}
                            width={width}
                            rowHeight={width / dashboardColumns - columnSpacing}
                            sidebarWidth={isSidebarExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH}
                            toolbarBottomY={toolbarBottomY}
                            componentTypeToTemplate={componentTypeToTemplate}
                            scrollToComponentRef={scrollToComponentRef}
                            onComponentLoadComplete={onComponentLoadComplete}
                        />
                        <EditComponentDrawer
                            open={!!componentInfoToEdit}
                            onRequestClose={closeEditComponentDrawer}
                            componentInfo={componentInfoToEdit}
                            dashboard={currentDashboard}
                            onDashboardChange={handleDashboardChange}
                            componentTypeToTemplate={componentTypeToTemplate}
                        />
                      </div>
                    </>
                ) : (
                    <div>
                      <NovoLoading />
                    </div>
                )}
            </div>
            {parentConfigHasChanged && (
                <div
                    style={{
                      background: theme.themeId === "light"
                          ? "rgba(241, 241, 241, 0.92)"
                          : "rgba(50, 50, 63, 0.92)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      position: "absolute",
                      paddingTop: "160px",
                      top: filtersExpanded ? 205 : 115,
                      left: 0,
                      width: "100%",
                      zIndex: 100,
                      bottom: 0
                    }}
                >
                  <div style={{textAlign: "center"}}>
                    <p style={{marginBottom: 0}}>You've updated your dashboard filters.</p>
                    <br />
                    <div>
                      <TextButton
                          type="secondary"
                          label="Cancel"
                          onClick={() => setLatestDashboardConfig(currentDashboard.getIn(["json", "dataConfig"]))}
                          style={{
                            fontSize: "12px",
                            border: `1px solid ${theme.palette.text.main}`,
                            color: theme.palette.text.main,
                            marginRight: "1rem"
                          }}
                      />
                      <TextButton
                          type="primary"
                          label="Load Latest Data"
                          onClick={() => handleLoadLatestData()}
                          style={{fontSize: "12px"}}
                      />
                    </div>
                  </div>
                </div>
            )}
          </div>
        </div>

        <EasyShareDrawer
            open={showSavedDashboards}
            onRequestClose={closeSavedDashboardsDrawer}
            headerLabel="Dashboards"
            configType="DASHBOARD"
            ownershipTypeToCustomDisplayName={ownershipTypeToCustomDisplayName}
            ownershipTypeToConfigsWrapper={ownershipTypeToDashboardsWrapper}
            setOwnershipTypeToConfigsWrapper={setOwnershipTypeToDashboardsWrapper}
            onSelect={switchToDashboard}
            onRemoved={afterDashboardDeleted}
            onRequestReload={loadDashboardsAndScheduledReports}
            isEditSharingPermissionEnabled={true}
            isSharingEnabled={Users.currentHasPermission("SHARE_DASHBOARD")}
            isSharingReadOnly={!Users.currentHasPermission("DASHBOARD_FULL_EDITOR")}
            isScheduledReportsEnabled={Users.currentHasPermission(Permissions.userPermissions.canScheduleReport)}
            onOpenScheduleDrawer={setScheduleConfigId}
            onEditSchedule={setScheduleIdToEdit}
            scheduledReports={dashboardIdToScheduledReports}
        />

        <AddComponentDrawer
            open={showComponentMenu}
            onRequestClose={closeComponentMenu}
            onRequestOpenAiAssistant={openAiAssistant}
            onAddComponent={handleAddComponent}
        />

        <AiAssistantDrawer
            queueDataLoad={queueDataLoad}
            clearDataLoad={clearDataLoad}
            dashboardConfig={currentDashboardConfig}
            messages={getMessagesForCurrentDashboard()}
            onStartOver={handleAiAssistantStartOver}
            onSendMessage={(message) => sendMessageToAIAssistant(message)}
            isLoading={Rata.isLoading(messagesToDashboardsWrapper)}
            hasError={Rata.hasError(messagesToDashboardsWrapper)}
            error={Rata.getError(messagesToDashboardsWrapper)}
            open={showAiAssistant}
            onRequestDialog={openDialog}
            onRequestClose={closeAiAssistant}
            onDashboardChange={handleDashboardChange}
            dashboard={currentDashboard}
            componentTypeToTemplate={componentTypeToTemplate}
            activeTabId={activeTabId}
        />
        {idToDashboard.count() > 0 && (
            <ScheduleDrawer
                open={!!scheduleConfigId}
                onRequestClose={closeScheduleDrawer}
                configId={scheduleConfigId}
                scheduleToEdit={dashboardIdToScheduledReports
                    .get(scheduleConfigId)
                    ?.find((r) => r.get("id") === scheduleIdToEdit)}
                cloneFromId={null}
                onCloneClick={null}
                idToDashboard={idToDashboard}
                SavedConfigs={SavedConfigs}
                onScheduleUpdate={handleScheduleUpdate}
                onScheduleDelete={handleScheduleDelete}
                existingSchedules={dashboardIdToScheduledReports
                    .valueSeq()
                    .flatMap((reports) => reports)
                    .map((report) =>
                        Immutable.Map({
                          name: report.get("name"),
                          dashboardId: report.get("dashboardId")
                        })
                    )}
            />
        )}

        {renderDialogs()}
      </div>
  );
};

const useDialogStack = () => {
  const [dialogStack, setDialogStack] = React.useState(Immutable.List());

  const closeDialog = React.useCallback((id) => {
    setDialogStack((dialogStack) => dialogStack.filter((dialog) => dialog.id !== id));
  }, []);

  const openDialog = React.useCallback(
      (content, {id, requireExistingId = false, title, width, minWidth, height, minHeight, titleStyle} = {}) => {
        id = id || Math.random();
        setDialogStack((dialogStack) => {
          if (requireExistingId) {
            return dialogStack.map((dialog) => {
              if (dialog.id === id) {
                title = title || dialog.title;
                return {id, content, title, width, minWidth, height, minHeight, titleStyle};
              } else {
                return dialog;
              }
            });
          } else {
            return dialogStack.push({id, content, title, width, minWidth, height, minHeight, titleStyle});
          }
        });
        return () => closeDialog(id);
      },
      [closeDialog]
  );

  const renderDialogs = React.useCallback(
      () => (
          <>
            {dialogStack
                .map(({id, title, content, width, minWidth, height, minHeight, titleStyle}) => (
                    <Dialog
                        key={id}
                        width={width}
                        minWidth={minWidth}
                        height={height}
                        minHeight={minHeight}
                        titleStyle={titleStyle}
                        label={title}
                        content={content}
                        onRequestClose={() => closeDialog(id)}
                    />
                ))
                .toArray()}
          </>
      ),
      [dialogStack, closeDialog]
  );

  return [renderDialogs, openDialog, closeDialog];
};

const componentTypeToTemplate = indexBy(
    (x) => x.get("type"),
    Immutable.List.of(
        trendTemplate,
        uiGridTemplate,
        asyncServerGridTemplate,
        metricCurveTemplate,
        ratioTemplate,
        ratioComparisonTemplate,
        leaderboardTemplate,
        textTemplate,
        performanceSnapshotTemplate,
        dividerTemplate
    )
);

const loadDashboards = () => SavedConfigs.getAll("DASHBOARD");

export default DashboardApp;
